import { Card, Flex } from "antd";
import { translate } from "shared/languages";
import { SHARED_LABEL } from "shared/utils";
import { tnum } from "shared/components/TableText";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { PackageExpiryRow, PackageType } from "shared/types";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { productNameLabel } from "shared/reports/columnCatalogue";

interface PackageExpiryTableProps {
  // Lifts the AntD sorter to the page so the PDF export mirrors the
  // on-screen order.
  onSortChange?: (sorter: unknown) => void;
  // Paket or Membership: names the product column.
  productType: PackageType;
  showRemainingCredits: boolean;
  rows: PackageExpiryRow[];
  loading: boolean;
  onRowClick: (row: PackageExpiryRow) => void;
  // This table renders every view of the two combined status reports
  // (paket/membership × expired/expiring soon, plus Kredit Akan Habis), so
  // each view names its own page-memory slot; the page keys the table per
  // view so a remount re-reads the right slot.
  pageStorageKey: string;
  // Which column leads after the client: "expiry" (the expiry views,
  // default), "credits" (Kredit Akan Habis, whose metric is Sisa Kredit —
  // that column moves forward and becomes sortable, default descend) or
  // "unactivated" (Belum Aktif: no expiry column at all; purchase date and
  // the auto-start date instead, sorted soonest auto-start first).
  emphasis?: "expiry" | "credits" | "unactivated";
}

// Shared table for the expiry reports ("recently expired" / "expiring soon")
// and Kredit Akan Habis — same columns, different data source and column
// emphasis. Columns come from the shared report catalogue.
const PackageExpiryTable = ({
  productType,
  showRemainingCredits,
  rows,
  loading,
  onRowClick,
  pageStorageKey,
  onSortChange,
  emphasis = "expiry",
}: PackageExpiryTableProps) => {
  const customerCol = reportColumn<PackageExpiryRow>("client", {
    dataIndex: "customer_name",
    // A shared package expiring strips access from everyone on it, so the row
    // has to say so. Naming just the buyer understates the loss: the studio
    // would read one client where three lost their credits.
    render: (name: string, row: PackageExpiryRow) => (
      <Flex align="center" gap={8} wrap="wrap">
        <span style={{ fontWeight: 600 }}>{name}</span>
        {(row.shared_with_count ?? 1) > 1 && (
          <StatusBadge
            tone="progress"
            label={SHARED_LABEL}
            dot={false}
          />
        )}
      </Flex>
    ),
  });
  const expiryCol = reportColumn<PackageExpiryRow>("expiry", {
    dataIndex: "expiry_date",
    sorter: (a, b) => (a.expiry_date ?? "").localeCompare(b.expiry_date ?? ""),
  });
  const purchaseCol = reportColumn<PackageExpiryRow>("purchase_date", {
    dataIndex: "purchase_date",
  });
  const autoStartCol = reportColumn<PackageExpiryRow>("auto_start", {
    dataIndex: "auto_start_date",
    sorter: (a, b) => (a.auto_start_date ?? "").localeCompare(b.auto_start_date ?? ""),
    defaultSortOrder: "ascend",
  });
  const packageCol = reportColumn<PackageExpiryRow>("product", {
    dataIndex: "package_name",
    label: () => productNameLabel(productType),
    render: (value: string) => value,
  });
  // Membership plans are unlimited — every row would just say "Tidak
  // Terbatas", which carries no information, so this column only makes
  // sense for Paket.
  const creditsCol = reportColumn<PackageExpiryRow>("remaining_credits", {
    dataIndex: "remaining_credits",
    render: (value: number | null) => (
      <span style={tnum}>
        {value === null ? translate("analyticsPage.package.unlimited") : value}
      </span>
    ),
    // Sortable only where it is the report's metric; the server already
    // sends Kredit Akan Habis rows in descending Sisa Kredit order.
    ...(emphasis === "credits"
      ? {
          sorter: (a: PackageExpiryRow, b: PackageExpiryRow) =>
            (a.remaining_credits ?? 0) - (b.remaining_credits ?? 0),
          defaultSortOrder: "descend" as const,
        }
      : {}),
  });
  const lastUsedCol = reportColumn<PackageExpiryRow>("last_used", {
    dataIndex: "last_used",
  });

  const columns: ReportColumn<PackageExpiryRow>[] =
    emphasis === "credits"
      ? [customerCol, packageCol, creditsCol, expiryCol, lastUsedCol]
      : emphasis === "unactivated"
        ? [
            customerCol,
            packageCol,
            purchaseCol,
            autoStartCol,
            ...(showRemainingCredits ? [creditsCol] : []),
          ]
        : [
          customerCol,
          expiryCol,
          packageCol,
          ...(showRemainingCredits ? [creditsCol] : []),
          lastUsedCol,
        ];

  return (
    <Card styles={{ body: { padding: 18 } }}>
      <ReportTable<PackageExpiryRow>
        // The customer_package id: the composite is NOT unique (same-batch
        // purchases share customer + package + expiry_date), and duplicate
        // keys make React duplicate rows on every re-sort. The fallback only
        // covers an older server that doesn't send `id` yet.
        rowKey={(row) => row.id ?? `${row.customer_id}-${row.package_name}-${row.expiry_date}`}
        loading={loading}
        columns={columns}
        dataSource={rows}
        pageMemory={pageStorageKey}
        onSortChange={onSortChange}
        onRow={(row) => ({
          onClick: () => onRowClick(row),
          style: { cursor: "pointer" },
        })}
      />
    </Card>
  );
};

export default PackageExpiryTable;
